// components/Notifier.jsx
'use client';
import { createContext, useContext, useState, useCallback, useRef } from 'react';
import { Box, Alert, Collapse, Chip, Fade } from '@mui/material';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import notificationSound from '../assets/sounds/notifSound.mp3';

const NotifierContext = createContext(null);

let snackIdCounter = 0;
const MAX_NOTIFS = 5;

export const NotifierProvider = ({ children }) => {
  const [snacks, setSnacks] = useState([]);

  const timers = useRef({});
  const lastDismissTimeRef = useRef(0);
  const lastSoundTimeRef = useRef(0);

  const handleClose = useCallback((id) => {
    setSnacks((prev) => prev.map((s) => (s.id === id ? { ...s, open: false } : s)));

    if (timers.current[id]) {
      clearTimeout(timers.current[id]);
      delete timers.current[id];
    }
    setTimeout(() => {
      setSnacks((prev) => prev.filter((snack) => snack.id !== id));
    }, 400);
  }, []);

  const handleCloseAll = useCallback(() => {
    setSnacks((prev) => prev.map((s) => ({ ...s, open: false })));

    Object.values(timers.current).forEach(clearTimeout);
    timers.current = {};
    lastDismissTimeRef.current = 0;

    setTimeout(() => {
      setSnacks([]);
    }, 400);
  }, []);

  const notify = useCallback(
    ({ message = '', severity = 'success' }) => {
      const id = ++snackIdCounter;

      const now = Date.now();
      let dismissTime = Math.max(now + 5000, lastDismissTimeRef.current + 2000);
      lastDismissTimeRef.current = dismissTime;

      const delay = dismissTime - now;

      setSnacks((prev) => {
        const newSnack = { id, message, severity, open: true, duration: delay };
        const updatedSnacks = [...prev, newSnack];
        if (updatedSnacks.length > MAX_NOTIFS) {
          return updatedSnacks.slice(updatedSnacks.length - MAX_NOTIFS);
        }
        return updatedSnacks;
      });

      try {
        const rawVolume = localStorage.getItem('notifSoundVolume');
        // Default to 50% if not set
        const volume = rawVolume !== null ? parseInt(rawVolume, 10) : 50;

        if (volume > 0) {
          // Add throttling logic: play sound only if at least 1000ms has passed since the last sound
          if (now - lastSoundTimeRef.current > 1000) {
            const audio = new Audio(notificationSound);
            audio.volume = volume / 100;
            audio.play().catch((e) => console.warn('Browser blocked notification sound', e));
            lastSoundTimeRef.current = now;
          }
        }
      } catch (err) {
        console.error('Failed to play sound', err);
      }

      timers.current[id] = setTimeout(() => {
        handleClose(id);
      }, delay);
    },
    [handleClose]
  );

  const severitySx = {
    success: { backgroundColor: '#2e7d32', color: '#fff' },
    error: { backgroundColor: '#d32f2f', color: '#fff' },
    warning: { backgroundColor: '#ed6c02', color: '#fff' },
    info: { backgroundColor: '#0288d1', color: '#fff' }
  };

  const activeSnacksCount = snacks.filter((s) => s.open).length;

  return (
    <NotifierContext.Provider value={notify}>
      {children}

      <Box
        sx={{
          position: 'fixed',
          top: 24,
          right: 24,
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          pointerEvents: 'none',
          alignItems: 'flex-end',
          maxWidth: 400
        }}
      >
        <Fade in={activeSnacksCount > 1}>
          <Box sx={{ pointerEvents: 'auto', mb: 1.5 }}>
            <Chip
              icon={<ClearAllIcon fontSize="small" />}
              label={`Close all (${activeSnacksCount})`}
              onClick={handleCloseAll}
              size="small"
              clickable
              sx={{
                bgcolor: 'rgba(253, 79, 79, 0.9)',
                backdropFilter: 'blur(8px)',
                color: '#ffffff',
                fontWeight: 700,
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                border: '1px solid rgba(0,0,0,0.05)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: '#9f0505',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 16px rgba(0,0,0,0.15)'
                },
                '& .MuiChip-icon': {
                  color: '#ffffff'
                }
              }}
            />
          </Box>
        </Fade>

        {snacks.map((snack) => (
          <Collapse key={snack.id} in={snack.open} timeout={400} unmountOnExit>
            <Box
              sx={{
                pb: 1.5,
                transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.4s ease-in-out',
                transform: snack.open ? 'translateX(0) scale(1)' : 'translateX(120%) scale(0.9)',
                opacity: snack.open ? 1 : 0,
                transformOrigin: 'right center'
              }}
            >
              <Box
                sx={{
                  pointerEvents: 'auto',
                  minWidth: 250,
                  maxWidth: 350,
                  position: 'relative',
                  borderRadius: 2,
                  overflow: 'hidden',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }}
              >
                <Alert
                  onClose={() => handleClose(snack.id)}
                  severity={snack.severity}
                  sx={{
                    width: '100%',
                    ...severitySx[snack.severity],
                    borderRadius: 0,
                    '& .MuiAlert-icon': {
                      color: '#fff',
                      opacity: 0.9
                    }
                  }}
                  elevation={0}
                  variant="filled"
                >
                  {snack.message}
                </Alert>
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    height: 4,
                    width: '100%',
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    display: 'flex',
                    justifyContent: 'flex-end'
                  }}
                >
                  <Box
                    sx={{
                      height: '100%',
                      backgroundColor: 'rgba(213, 213, 213, 0.9)',
                      '@keyframes progressShrink': {
                        '0%': { width: '100%' },
                        '100%': { width: '0%' }
                      },
                      animation: snack.open ? `progressShrink ${snack.duration}ms linear forwards` : 'none'
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Collapse>
        ))}
      </Box>
    </NotifierContext.Provider>
  );
};

export const useNotifier = () => {
  const ctx = useContext(NotifierContext);
  if (!ctx) {
    throw new Error('useNotifier must be used inside NotifierProvider');
  }
  return ctx;
};
